// Bespoke, project-branded PDF report (dark header banner, KPI cards, a
// financial grid with colored type badges, and a profit donut) — matching
// the approved reference design. Deliberately separate from the shared
// buildReportPdf() in ReportPdfButton.jsx, which other report pages use and
// must keep its own plain look.
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const NAVY_DARK   = [12, 35, 64];
const NAVY_LINE   = [56, 74, 102];
const TEAL        = [0, 163, 137];
const TEAL_CHIP   = [16, 62, 58];
const RED         = [230, 57, 70];
const RED_BG      = [253, 232, 234];
const BLUE        = [29, 78, 216];
const BLUE_BG     = [239, 246, 255];
const GRAY_TEXT   = [71, 85, 105];
const GRAY_BG     = [241, 245, 249];
const BORDER      = [226, 232, 240];
const MUTED       = [100, 116, 139];
const SLATE_400   = [148, 163, 184];
const SLATE_300   = [203, 213, 225];
const SLATE_900   = [15, 23, 42];
const WHITE       = [255, 255, 255];
const CARD_BG     = [248, 250, 252];
const DONE_DOT    = [16, 185, 129];
const DONE_BG     = [236, 253, 245];
const DONE_TEXT   = [4, 120, 87];
const PEND_DOT    = [148, 163, 184];
const PEND_BG     = [241, 245, 249];
const PEND_TEXT   = [71, 85, 105];

const TYPE_STYLE = {
  Revenue: { bg: GRAY_BG, color: GRAY_TEXT },
  Auto:    { bg: GRAY_BG, color: GRAY_TEXT },
  Cost:    { bg: RED_BG,  color: RED },
  Invoice: { bg: BLUE_BG, color: BLUE },
};

function nowLabel() {
  return new Date().toLocaleString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

function slugify(str) {
  return (str || 'Report')
    .split(/[^a-zA-Z0-9]+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join('-');
}

async function loadImageAsBase64(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext('2d').drawImage(img, 0, 0);
        resolve({ data: canvas.toDataURL('image/png'), w: img.naturalWidth, h: img.naturalHeight });
      } catch { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function setFill(doc, rgb) { doc.setFillColor(rgb[0], rgb[1], rgb[2]); }
function setText(doc, rgb) { doc.setTextColor(rgb[0], rgb[1], rgb[2]); }
function setDraw(doc, rgb) { doc.setDrawColor(rgb[0], rgb[1], rgb[2]); }

// Truncates text with an ellipsis so it never overflows maxWidth.
function fitText(doc, text, maxWidth) {
  const str = String(text ?? '');
  if (doc.getTextWidth(str) <= maxWidth) return str;
  let lo = 0, hi = str.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (doc.getTextWidth(`${str.slice(0, mid)}…`) <= maxWidth) lo = mid; else hi = mid - 1;
  }
  return lo > 0 ? `${str.slice(0, lo)}…` : '…';
}

function drawCard(doc, x, y, w, h) {
  setFill(doc, WHITE);
  setDraw(doc, BORDER);
  doc.setLineWidth(0.25);
  doc.roundedRect(x, y, w, h, 1.5, 1.5, 'FD');
}

function sectionLabel(doc, x, y, text) {
  setFill(doc, NAVY_DARK);
  doc.circle(x + 1, y - 1.3, 1, 'F');
  doc.setFontSize(8).setFont('helvetica', 'bold');
  setText(doc, [51, 65, 85]);
  doc.text(String(text).toUpperCase(), x + 5, y);
}

// Measures a pill's width without drawing it — used to budget layout space
// (e.g. the title's max width) before the pill itself is drawn.
function measurePillWidth(doc, text, fontSize, padX) {
  doc.setFontSize(fontSize).setFont('helvetica', 'bold');
  return doc.getTextWidth(text) + padX * 2;
}

// Rounded pill; returns the width it used.
function drawPill(doc, x, yTop, text, { bg, color, fontSize = 7, padX = 2.4, height = 4.4 }) {
  const w = measurePillWidth(doc, text, fontSize, padX);
  setFill(doc, bg);
  doc.roundedRect(x, yTop, w, height, height / 2, height / 2, 'F');
  setText(doc, color);
  doc.text(text, x + w / 2, yTop + height / 2 + fontSize * 0.16, { align: 'center' });
  return w;
}

function polar(cx, cy, r, thetaDeg) {
  const t = (thetaDeg * Math.PI) / 180;
  return { x: cx + r * Math.sin(t), y: cy - r * Math.cos(t) };
}

// Filled pie wedge from startDeg to endDeg (clockwise from 12 o'clock).
function drawWedge(doc, cx, cy, r, startDeg, endDeg, rgb) {
  const span = endDeg - startDeg;
  if (span <= 0) return;
  const steps = Math.max(2, Math.ceil(span / 6));
  setFill(doc, rgb);
  let prev = polar(cx, cy, r, startDeg);
  for (let i = 1; i <= steps; i++) {
    const cur = polar(cx, cy, r, startDeg + (span * i) / steps);
    doc.triangle(cx, cy, prev.x, prev.y, cur.x, cur.y, 'F');
    prev = cur;
  }
}

// Donut: profitPct (0-100) drawn teal, the remainder drawn red, center punched white.
function drawDonut(doc, cx, cy, outerR, innerR, profitPct) {
  const pct = Math.max(0, Math.min(100, profitPct || 0));
  setFill(doc, TEAL);
  doc.circle(cx, cy, outerR, 'F');
  if (pct < 100) drawWedge(doc, cx, cy, outerR, (pct / 100) * 360, 360, RED);
  setFill(doc, WHITE);
  doc.circle(cx, cy, innerR, 'F');
}

function drawItemList(doc, x, y, w, h, items) {
  const pad = 5;
  const rowH = h > pad * 2 && items.length ? Math.min(9, (h - pad * 2) / items.length) : 9;
  if (!items.length) {
    doc.setFontSize(8).setFont('helvetica', 'italic');
    setText(doc, MUTED);
    doc.text('None yet', x + pad, y + pad + 4);
    return;
  }
  items.forEach((it, i) => {
    const ry = y + pad + i * rowH + 3;
    setFill(doc, it.done ? DONE_DOT : PEND_DOT);
    doc.circle(x + pad + 1, ry - 1, 1, 'F');
    const pillLabel = (it.statusLabel || '').toUpperCase();
    doc.setFontSize(6).setFont('helvetica', 'bold');
    const pillW = pillLabel ? doc.getTextWidth(pillLabel) + 4.4 : 0;
    const titleMaxW = w - pad * 2 - 5 - (pillW ? pillW + 3 : 0);
    doc.setFontSize(8.3).setFont('helvetica', 'bold');
    setText(doc, SLATE_900);
    doc.text(fitText(doc, it.title, titleMaxW), x + pad + 5, ry);
    if (it.subtitle) {
      doc.setFontSize(6.8).setFont('helvetica', 'normal');
      setText(doc, MUTED);
      doc.text(fitText(doc, it.subtitle, titleMaxW), x + pad + 5, ry + 3.4);
    }
    if (pillLabel) {
      drawPill(doc, x + w - pad - pillW, ry - 3, pillLabel, {
        bg: it.done ? DONE_BG : PEND_BG, color: it.done ? DONE_TEXT : PEND_TEXT, fontSize: 6, height: 4,
      });
    }
  });
}

function drawKpiCards(doc, x, y, w, cards) {
  const gap = 3;
  const cw = (w - gap * (cards.length - 1)) / cards.length;
  const ch = 23;
  cards.forEach((c, i) => {
    const cx = x + i * (cw + gap);
    drawCard(doc, cx, y, cw, ch);
    doc.setFontSize(5.8).setFont('helvetica', 'bold');
    setText(doc, MUTED);
    doc.text(fitText(doc, c.label.toUpperCase(), cw - 6), cx + 3, y + 5);
    doc.setFontSize(11).setFont('helvetica', 'bold');
    setText(doc, c.color || SLATE_900);
    doc.text(fitText(doc, c.value, cw - 6), cx + 3, y + 12.5);
    if (c.progressPct != null) {
      setFill(doc, BORDER);
      doc.roundedRect(cx + 3, y + 15.5, cw - 6, 1.2, 0.6, 0.6, 'F');
      setFill(doc, NAVY_DARK);
      const pw = Math.max(1.2, ((cw - 6) * Math.min(100, Math.max(0, c.progressPct))) / 100);
      doc.roundedRect(cx + 3, y + 15.5, pw, 1.2, 0.6, 0.6, 'F');
      doc.setFontSize(5.8).setFont('helvetica', 'normal');
      setText(doc, MUTED);
      doc.text(fitText(doc, c.sub || '', cw - 6), cx + 3, y + 20);
    } else if (c.sub) {
      doc.setFontSize(5.8).setFont('helvetica', 'normal');
      setText(doc, MUTED);
      const subLines = doc.splitTextToSize(c.sub, cw - 6);
      doc.text(subLines.slice(0, 2), cx + 3, y + 17.2);
    }
  });
  return ch;
}

function drawFinancialTable(doc, x, y, w, fin) {
  const body = fin.rows.map((r) => [r.no, r.item, '', r.date || '—', r.estimate ?? '—', r.actual ?? '—', r.cost ?? '—']);
  autoTable(doc, {
    startY: y,
    head: [['NO', 'ITEM', 'TYPE', 'DATE', 'ESTIMATE', 'ACTUAL (AMT)', 'COST']],
    body,
    foot: [['', 'Total', '', '', fin.total.estimate, fin.total.actual, fin.total.cost]],
    margin: { left: x, right: doc.internal.pageSize.getWidth() - x - w },
    styles: {
      fontSize: 8, cellPadding: { top: 3.2, right: 3, bottom: 3.2, left: 3 },
      textColor: SLATE_900, lineColor: BORDER, lineWidth: 0.1,
    },
    headStyles: { fillColor: CARD_BG, textColor: MUTED, fontStyle: 'bold', fontSize: 7 },
    footStyles: { fillColor: CARD_BG, textColor: SLATE_900, fontStyle: 'bold', fontSize: 8 },
    columnStyles: {
      0: { cellWidth: 11, halign: 'center', textColor: MUTED, cellPadding: { top: 3.2, right: 1, bottom: 3.2, left: 1 } },
      1: { cellWidth: 'auto', fontStyle: 'bold' },
      2: { cellWidth: 20, halign: 'center' },
      3: { cellWidth: 24, halign: 'center', textColor: MUTED },
      4: { cellWidth: 22, halign: 'right' },
      5: { cellWidth: 24, halign: 'right' },
      6: { cellWidth: 22, halign: 'right' },
    },
    didParseCell: (data) => {
      if (data.section === 'foot') {
        if (data.column.index === 5) data.cell.styles.textColor = BLUE;
        if (data.column.index === 6) data.cell.styles.textColor = RED;
        return;
      }
      if (data.section !== 'body') return;
      const meta = fin.rows[data.row.index];
      if (!meta) return;
      if (data.column.index === 5) data.cell.styles.textColor = meta.actual && meta.actual !== '—' ? BLUE : MUTED;
      if (data.column.index === 6) data.cell.styles.textColor = meta.cost && meta.cost !== '—' ? RED : MUTED;
      if (data.column.index === 2) data.cell.text = [''];
    },
    didDrawCell: (data) => {
      if (data.section !== 'body' || data.column.index !== 2) return;
      const meta = fin.rows[data.row.index];
      const style = meta && TYPE_STYLE[meta.type];
      if (!style) return;
      const label = meta.type.toUpperCase();
      doc.setFontSize(6).setFont('helvetica', 'bold');
      const pw = doc.getTextWidth(label) + 4.4;
      const px = data.cell.x + (data.cell.width - pw) / 2;
      const py = data.cell.y + (data.cell.height - 3.6) / 2;
      setFill(doc, style.bg);
      doc.roundedRect(px, py, pw, 3.6, 1.8, 1.8, 'F');
      setText(doc, style.color);
      doc.text(label, px + pw / 2, py + 2.6, { align: 'center' });
    },
  });
  return doc.lastAutoTable.finalY;
}

function drawProfitViz(doc, x, y, w, fin) {
  const h = 34;
  drawCard(doc, x, y, w, h);
  const isProfit = fin.profit >= 0;
  const accent = isProfit ? TEAL : RED;

  const cx = x + 22, cy = y + h / 2;
  drawDonut(doc, cx, cy, 13, 8, fin.marginPct);
  doc.setFontSize(9).setFont('helvetica', 'bold');
  setText(doc, SLATE_900);
  doc.text(fin.marginPct != null ? `${fin.marginPct}%` : '—', cx, cy - 0.5, { align: 'center' });
  doc.setFontSize(5.2).setFont('helvetica', 'bold');
  setText(doc, MUTED);
  doc.text('MARGIN', cx, cy + 3, { align: 'center' });

  let ly = y + h / 2 - 5;
  const lx = x + 42;
  setFill(doc, TEAL); doc.rect(lx, ly - 2.6, 3, 3, 'F');
  doc.setFontSize(8).setFont('helvetica', 'bold'); setText(doc, SLATE_900);
  doc.text('Profit', lx + 5, ly);
  doc.setFontSize(8).setFont('helvetica', 'normal'); setText(doc, MUTED);
  doc.text(fin.profitLabel, lx + 22, ly);

  ly += 7;
  setFill(doc, RED); doc.rect(lx, ly - 2.6, 3, 3, 'F');
  doc.setFontSize(8).setFont('helvetica', 'bold'); setText(doc, SLATE_900);
  doc.text('Cost', lx + 5, ly);
  doc.setFontSize(8).setFont('helvetica', 'normal'); setText(doc, MUTED);
  doc.text(fin.costLabel, lx + 22, ly);

  const divX = x + w * 0.6;
  setDraw(doc, BORDER); doc.setLineWidth(0.2);
  doc.line(divX, y + 5, divX, y + h - 5);

  const tx = divX + 6;
  doc.setFontSize(6.5).setFont('helvetica', 'bold'); setText(doc, accent);
  doc.text(isProfit ? 'NET PROFIT' : 'NET LOSS', tx, y + 10);
  doc.setFontSize(15).setFont('helvetica', 'bold'); setText(doc, accent);
  doc.text(fin.profitLabel, tx, y + 19);
  doc.setFontSize(6.5).setFont('helvetica', 'normal'); setText(doc, MUTED);
  doc.text(`Revenue ${fin.revenueLabel} - Cost ${fin.costLabel}`, tx, y + 25);

  return h;
}

// project:    { name, statusLabel, client, owner, startDate, endDate, members }
// milestones: [{ title, subtitle, statusLabel, done }]
// tasks:      [{ title, subtitle, statusLabel, done }]
// financials: null | { cards, rows, total, marginPct, profit, profitLabel, revenueLabel, costLabel }
export async function buildProjectReportPdf({ orgName, logoSrc, project, milestones = [], tasks = [], financials = null }) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 14;
  const contentW = pageW - margin * 2;
  const footerZone = 14;
  let y = 0;

  function ensureRoom(h) {
    if (y + h > pageH - footerZone) {
      doc.addPage();
      y = 8;
    }
  }

  const logoLoaded = logoSrc ? await loadImageAsBase64(logoSrc) : null;

  // ── Header banner ─────────────────────────────────────────────────────
  // Client / Owner / Duration / Members live in the banner itself — no
  // separate "Project Info" card below it.
  const duration = (project.startDate || project.endDate)
    ? `${project.startDate || '—'} - ${project.endDate || '—'}`
    : null;
  const metaPills = [];
  if (project.client) metaPills.push(['CLIENT', project.client]);
  if (project.owner)  metaPills.push(['OWNER', project.owner]);
  if (duration)       metaPills.push(['DURATION', duration]);

  doc.setFontSize(8.5).setFont('helvetica', 'normal');
  const membersMaxW = contentW - 22;
  const membersLines = doc.splitTextToSize(project.members || '—', membersMaxW);

  // Measure the right-aligned meta block and the status pill up front so
  // the title knows how much width it actually has to work with. The title
  // itself wraps (never truncates) to as many lines as it needs.
  const statusText = project.statusLabel ? project.statusLabel.toUpperCase() : '';
  const statusPillW = statusText ? measurePillWidth(doc, statusText, 7.5, 2.4) : 0;
  let metaBlockW = 0;
  metaPills.forEach(([label, value], i) => {
    if (i > 0) metaBlockW += 5;
    doc.setFontSize(6.2).setFont('helvetica', 'bold');
    const labelW = doc.getTextWidth(label);
    doc.setFontSize(9).setFont('helvetica', 'bold');
    const valW = doc.getTextWidth(fitText(doc, value, 60));
    metaBlockW += Math.max(labelW, valW);
    if (i < metaPills.length - 1) metaBlockW += 8;
  });

  // Long titles get squeezed by the meta block reserved on the same row, so
  // the title font shrinks in steps (16pt down to an 11pt floor) until it
  // fits in 2 lines rather than wrapping into 3-4 cramped ones.
  const TITLE_FONT_MAX = 16;
  const TITLE_FONT_MIN = 11;
  const rightReserve = metaPills.length ? metaBlockW + 12 : 0;
  const titleMaxW = Math.max(30, contentW - (statusText ? statusPillW + 10 : 15) - rightReserve);
  let titleFontSize = TITLE_FONT_MAX;
  let titleLines;
  for (;;) {
    doc.setFontSize(titleFontSize).setFont('helvetica', 'bold');
    titleLines = doc.splitTextToSize(project.name || '', titleMaxW);
    if (titleLines.length <= 2 || titleFontSize <= TITLE_FONT_MIN) break;
    titleFontSize -= 1;
  }
  const titleLineH = Math.round(titleFontSize * 0.4375 * 10) / 10;
  const titleFirstY = 27;
  const titleLastY  = titleFirstY + (titleLines.length - 1) * titleLineH;

  // Members sits on its own row below the (possibly multi-line) title row,
  // clear of the status pill's bottom edge (titleLastY + 1.4); Client/Owner/
  // Duration share the title's first line, right-aligned.
  const metaTopY    = titleLastY + 8;
  const membersRowH = 5 + (membersLines.length - 1) * 4.2;
  const bannerH = metaTopY + membersRowH;

  setFill(doc, NAVY_DARK);
  doc.rect(0, 0, pageW, bannerH, 'F');

  // Real org logo when available; otherwise a small drawn diamond mark —
  // jsPDF's built-in helvetica only covers WinAnsi, so a Unicode ◆ glyph
  // would render as mojibake.
  let markW = 5;
  if (logoLoaded) {
    const maxH = 9;
    const lw = maxH * (logoLoaded.w / logoLoaded.h);
    doc.addImage(logoLoaded.data, 'PNG', margin, 4.5, lw, maxH);
    markW = lw + 4;
  } else {
    setFill(doc, TEAL);
    const markCx = margin + 1.2, markCy = 9.6, markR = 1.6;
    doc.triangle(markCx, markCy - markR, markCx + markR, markCy, markCx, markCy + markR, 'F');
    doc.triangle(markCx, markCy - markR, markCx, markCy + markR, markCx - markR, markCy, 'F');
  }
  doc.setFontSize(10).setFont('helvetica', 'bold');
  setText(doc, WHITE);
  doc.text(String(orgName || '').toUpperCase(), margin + markW, 12);

  doc.setFontSize(8).setFont('helvetica', 'normal');
  setText(doc, SLATE_300);
  doc.text(`Generated: ${nowLabel()}`, pageW - margin, 12, { align: 'right' });

  setDraw(doc, NAVY_LINE);
  doc.setLineWidth(0.2);
  doc.line(margin, 16, pageW - margin, 16);

  // Title (wraps across as many lines as it needs — never truncated) with
  // the status pill after its last line; Client/Owner/Duration right-align
  // on the title's first line.
  doc.setFontSize(titleFontSize).setFont('helvetica', 'bold');
  setText(doc, WHITE);
  titleLines.forEach((line, i) => doc.text(line, margin, titleFirstY + i * titleLineH));
  const lastLineW = doc.getTextWidth(titleLines[titleLines.length - 1] || '');
  if (statusText) {
    drawPill(doc, margin + lastLineW + 5, titleLastY - titleFontSize * 0.25, statusText, {
      bg: TEAL_CHIP, color: TEAL, fontSize: 7.5, height: titleFontSize * 0.3375,
    });
  }

  if (metaPills.length) {
    let px = pageW - margin - metaBlockW;
    metaPills.forEach(([label, value], i) => {
      if (i > 0) {
        setDraw(doc, NAVY_LINE);
        doc.setLineWidth(0.2);
        doc.line(px, 19.5, px, 25);
        px += 5;
      }
      doc.setFontSize(6.2).setFont('helvetica', 'bold');
      setText(doc, SLATE_400);
      doc.text(label, px, 22);
      doc.setFontSize(9).setFont('helvetica', 'bold');
      setText(doc, [241, 245, 249]);
      const val = fitText(doc, value, 60);
      doc.text(val, px, titleFirstY);
      px += Math.max(doc.getTextWidth(val), doc.getTextWidth(label)) + 8;
    });
  }

  doc.setFontSize(6.2).setFont('helvetica', 'bold');
  setText(doc, SLATE_400);
  doc.text('MEMBERS', margin, metaTopY);
  doc.setFontSize(8.5).setFont('helvetica', 'normal');
  setText(doc, [226, 232, 240]);
  doc.text(membersLines, margin + 22, metaTopY);

  y = bannerH + 8;

  // ── Milestones + Tasks ───────────────────────────────────────────────
  const halfW = (contentW - 6) / 2;
  const pad = 5;
  const listRowH = 9;
  const msH = pad * 2 + Math.max(1, milestones.length) * listRowH;
  const tkH = pad * 2 + Math.max(1, tasks.length) * listRowH;
  const pairH = Math.max(msH, tkH);
  ensureRoom(6 + pairH + 8);
  sectionLabel(doc, margin, y, 'Milestones');
  sectionLabel(doc, margin + halfW + 6, y, 'Tasks');
  y += 6;
  drawCard(doc, margin, y, halfW, pairH);
  drawCard(doc, margin + halfW + 6, y, halfW, pairH);
  drawItemList(doc, margin, y, halfW, pairH, milestones);
  drawItemList(doc, margin + halfW + 6, y, halfW, pairH, tasks);
  y += pairH + 8;

  // ── Financial overview + grid + profit visualization ────────────────
  if (financials) {
    ensureRoom(6 + 23 + 8);
    sectionLabel(doc, margin, y, 'Financial Overview');
    y += 6;
    const kpiH = drawKpiCards(doc, margin, y, contentW, financials.cards);
    y += kpiH + 8;

    ensureRoom(6 + 20);
    sectionLabel(doc, margin, y, 'Financial Summary');
    y += 6;
    y = drawFinancialTable(doc, margin, y, contentW, financials) + 8;

    ensureRoom(6 + 34 + 8);
    sectionLabel(doc, margin, y, 'Profit Visualization');
    y += 6;
    const vizH = drawProfitViz(doc, margin, y, contentW, financials);
    y += vizH + 8;
  }

  // ── Footer on every page ─────────────────────────────────────────────
  const totalPages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    setDraw(doc, BORDER); doc.setLineWidth(0.2);
    doc.line(margin, pageH - 14, pageW - margin, pageH - 14);
    doc.setFontSize(7).setFont('helvetica', 'normal'); setText(doc, MUTED);
    const pageLabel = `Page ${p} of ${totalPages}`;
    const leftMaxW = contentW - doc.getTextWidth(pageLabel) - 6;
    doc.text(fitText(doc, `${orgName} — Project Report — ${project.name}`, leftMaxW), margin, pageH - 9);
    doc.text(pageLabel, pageW - margin, pageH - 9, { align: 'right' });
  }

  doc.save(`${slugify(`Project-Report-${project.name}`)}-${new Date().toISOString().slice(0, 10)}.pdf`);
}
